// Shared between worker and web. Mirrors the PocketBase schema in tools/setup.

// ─── pipeline state ───────────────────────────────────────────────────────────

/**
 * One state machine for the whole campaign, replacing v1's two independent
 * columns (Script Status × Video Status) that could contradict each other —
 * the live sheet has a row reading "Reviewing" + "Published" simultaneously.
 */
export type Stage =
  | 'draft'
  | 'script_generating'
  | 'script_review'
  | 'images_rendering'
  | 'image_review'
  | 'videos_rendering'
  | 'video_review'
  | 'assembling'
  | 'transcribing'
  | 'captioning'
  | 'publishing'
  | 'done'
  | 'failed'
  | 'needs_attention'
  | 'cancelled';

/** Stages where the pipeline is waiting on a person. No process is running. */
export const REVIEW_STAGES: Stage[] = ['script_review', 'image_review', 'video_review'];

/** Stages where a job is (or should be) in flight. */
export const ACTIVE_STAGES: Stage[] = [
  'script_generating',
  'images_rendering',
  'videos_rendering',
  'assembling',
  'transcribing',
  'captioning',
  'publishing',
];

export const TERMINAL_STAGES: Stage[] = ['done', 'failed', 'cancelled'];

export const STAGE_LABELS: Record<Stage, string> = {
  draft: 'Draft',
  script_generating: 'Writing script',
  script_review: 'Script review',
  images_rendering: 'Rendering images',
  image_review: 'Image review',
  videos_rendering: 'Rendering video',
  video_review: 'Video review',
  assembling: 'Assembling',
  transcribing: 'Transcribing',
  captioning: 'Adding captions',
  publishing: 'Publishing',
  done: 'Done',
  failed: 'Failed',
  needs_attention: 'Needs attention',
  cancelled: 'Cancelled',
};

// ─── jobs ─────────────────────────────────────────────────────────────────────

export type JobKind =
  | 'generate_script'
  | 'render_images'
  | 'process_image_review'
  | 'render_videos'
  | 'process_video_review'
  | 'assemble'
  | 'transcribe'
  | 'caption'
  | 'publish'
  | 'stitch'
  | 'sheet_test';

/** ffmpeg saturates a core; these are limited separately from API-bound work. */
export const MEDIA_JOB_KINDS: JobKind[] = ['assemble', 'transcribe', 'caption', 'stitch'];

// ─── stitcher ─────────────────────────────────────────────────────────────────

/**
 * xfade transition names, plus `cut` for a hard join.
 *
 * An allowlist, not free text: v1 interpolated this value straight into the
 * ffmpeg `filter_complex` string that was passed to a shell, which made it a
 * command-injection vector.
 */
export const TRANSITIONS = [
  'cut',
  'fade',
  'dissolve',
  'wipeleft',
  'wiperight',
  'wipeup',
  'wipedown',
  'slideleft',
  'slideright',
  'slideup',
  'slidedown',
  'circleopen',
  'circleclose',
  'distance',
] as const;
export type Transition = (typeof TRANSITIONS)[number];

export const SFX_OPTIONS = ['none', 'whoosh', 'whip', 'flash', 'ambient', 'click'] as const;
export type Sfx = (typeof SFX_OPTIONS)[number];

export type StitchStatus = 'queued' | 'processing' | 'done' | 'failed';

export interface StitchJob {
  id: string;
  user: string;
  hook: string;
  main: string;
  transition: Transition;
  transition_speed: number;
  sfx: Sfx;
  status: StitchStatus;
  result: string;
  log: string;
  error: string;
  created?: string;
  updated?: string;
}

export type JobStatus = 'queued' | 'claimed' | 'done' | 'failed' | 'cancelled';

export interface Job {
  id: string;
  kind: JobKind;
  run: string;
  campaign: string;
  owner: string;
  payload: Record<string, unknown>;
  status: JobStatus;
  attempts: number;
  max_attempts: number;
  last_error?: string;
}

// ─── runs & logging ───────────────────────────────────────────────────────────

export type RunStatus = 'running' | 'succeeded' | 'failed' | 'cancelled' | 'cancelling';

export interface Run {
  id: string;
  expand?: {
    campaign?: Campaign;
    started_by?: User;
  };
  created?: string;
  campaign: string;
  run_number: number;
  started_by: string;
  trigger: string;
  status: RunStatus;
  current_stage: Stage | '';
  started: string;
  ended?: string;
  stats?: RunStats;
  error?: string;
}

export interface RunStats {
  images_generated?: number;
  images_failed?: number;
  videos_generated?: number;
  videos_failed?: number;
  api_calls?: number;
  retries?: number;
  review_rounds?: number;
  /**
   * USD accrued by this run, added when a generation COMPLETES — kie.ai only
   * bills finished generations, so a task that fails on their end costs
   * nothing. A campaign that later fails or is abandoned keeps the cost of
   * the generations that did finish. Reused library images never accrue.
   */
  cost_usd?: number;
}

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * One line of the run log. Written the moment it happens, never batched — this
 * is why a cancelled run still has a readable history, which n8n could not do.
 */
export interface PipelineEvent {
  id: string;
  run: string;
  campaign: string;
  seq: number;
  stage: string;
  level: LogLevel;
  /** Short machine-ish label: `stage_begin`, `api_call`, `retry`, `failed`. */
  event: string;
  message: string;
  detail?: Record<string, unknown>;
  duration_ms?: number;
  created: string;
}

// ─── domain ───────────────────────────────────────────────────────────────────

export interface Scene {
  sceneIndex: number;
  action: string;
  speech: string;
  location_tag: string;
  holding_product?: 'YES' | 'NO';
  /**
   * A previously generated image to reuse instead of paying for a new one.
   * When set, the first image pass skips generation for this scene entirely;
   * the location system is untouched — this simply sits beside it as a
   * per-scene alternative. An explicit re-render request always generates
   * fresh (the reviewer asked for something new).
   */
  pinned_image_url?: string;
}

export type Gender = 'male' | 'female' | 'neutral';

export interface Influencer {
  id: string;
  influencer_id: string;
  name: string;
  base_image_url: string;
  avatar_url: string;
  character_lock: string;
  setting_lock: string;
  /**
   * v1 hardcoded "She holds a small supplement bottle" into all four image
   * prompt builders, regardless of who the influencer was.
   */
  gender: Gender;
  prompt: string;
  guidelines: string;
  custom_prompt: string;
  /** Empty = visible to everyone. */
  allowed_users: string[];
  /** Who made it in the avatar creator; empty on imported/admin-made rows. */
  created_by?: string;
  active: boolean;
}

export interface Location {
  id: string;
  influencer: string;
  name: string;
  tag: string;
  /** Optional "when to use this room" hint, e.g. "Explaining science, giving advice". */
  usage_hint?: string;
  drive_url: string;
  is_global: boolean;
  sort: number;
}

export interface Product {
  id: string;
  product_id: string;
  brand: string;
  name: string;
  link: string;
  benefits: string;
  image_url: string;
  /** v1 never loaded this — it read `Products!A:G` but the column is H. */
  guidelines: string;
  /** Who made it in the product creator; empty on imported/admin-made rows. */
  created_by?: string;
  active: boolean;
}

export type AspectRatio = '9:16' | '16:9';
export type CampaignMode = 'ai' | 'custom';

export interface Campaign {
  id: string;
  /** Populated when the query asks for `expand: 'influencer,product'`. */
  expand?: {
    influencer?: Influencer;
    product?: Product;
    created_by?: User;
  };
  seq: number;
  title: string;
  product: string;
  influencer: string;
  created_by: string;
  mode: CampaignMode;
  angle: string;
  custom_script: string;
  aspect_ratio: AspectRatio;
  custom_locations: string[];
  scenes: Scene[];
  stage: Stage;
  stage_message: string;
  /** Snapshot of the prompt text actually used, for auditability. */
  resolved_prompts?: Record<string, string>;
  /** Both variants are kept and both are offered to the user. */
  final_video_url: string;
  final_video_id: string;
  final_video_url_uncaptioned: string;
  final_video_id_uncaptioned: string;
  published_at?: string;
  /**
   * A presentation campaign: visible to admins only, excluded from
   * statistics, and every generation stage swaps in the pre-made assets from
   * Admin → Demo assets instead of calling a provider. Nothing is billed.
   */
  is_demo?: boolean;
  /**
   * Set on a revision: the finished campaign this one was cloned from via
   * "Fix a scene". The parent is never rewound — it stays the record of what
   * was delivered — and the clone starts at video review with every clip
   * carried over and pre-approved. `parent_seq` is denormalised so the list
   * can label it without an extra fetch. Optional: installs upgraded before
   * this existed have no such fields until setup re-runs.
   */
  parent_campaign?: string;
  parent_seq?: number;
  created: string;
  updated: string;
}

export type ReviewKind = 'image' | 'video';

/**
 * v1 matched decisions as exact lowercase strings, so a typo silently counted as
 * an approval. These are a PocketBase select field — anything else is rejected.
 */
export type Decision = '' | 'approve' | 'rerender' | 'rerender_image';

export interface SceneReview {
  id: string;
  campaign: string;
  scene_index: number;
  kind: ReviewKind;
  media_url: string;
  drive_url: string;
  speech: string;
  decision: Decision;
  note: string;
  prompt_edit: string;
  holding_product: '' | 'YES' | 'NO';
  /**
   * The location this image was generated in — recorded so the image library
   * can suggest reusable images from the same room.
   */
  location_tag: string;
  in_point: number;
  out_point: number;
  review_round: number;
  decided_by: string;
  decided_at?: string;
  /** Old imported rows whose tempfile.aiquickdraw.com links have expired. */
  media_expired: boolean;
  failed: boolean;
  fail_reason: string;
  created?: string;
  updated?: string;
  expand?: {
    campaign?: Campaign;
  };
}

export type UserRole = 'admin' | 'member';

export interface User {
  id: string;
  email: string;
  /** PocketBase's built-in field on the `users` collection. */
  name: string;
  avatar_url: string;
  role: UserRole;
}

// ─── settings ─────────────────────────────────────────────────────────────────

export interface SubtitleLayout {
  /**
   * Must be a font installed in the worker image. v1 asked for Arial, which is
   * not present on a Debian base — libass silently substituted whatever it
   * could find. The image installs the DejaVu family.
   */
  fontName: string;
  fontSize: number;
  marginV: number;
  marginLR: number;
  maxChars: number;
  maxWords: number;
  outline: number;
}

// ─── demo asset bank ──────────────────────────────────────────────────────────

/** One scripted scene of the demo, with the media the worker hands out for it. */
export interface DemoScene {
  action: string;
  speech: string;
  location_tag: string;
  holding_product: 'YES' | 'NO';
  /**
   * Image shown for this scene, by review round: `images[0]` is the first
   * render, `images[1]` is what a "re-render" request produces, and so on.
   * A round past the end of the list reuses the last entry.
   */
  images: string[];
  /** Same idea for the animated clip. */
  videos: string[];
}

export interface DemoBank {
  /** How long each fake stage "works" before it finishes, so the audience sees it. */
  delays: {
    scriptSeconds: number;
    imageSecondsPerScene: number;
    videoSecondsPerScene: number;
    /** Split across assembling → transcribing → captioning → publishing. */
    finishSeconds: number;
  };
  /**
   * Pretend prices so the "Current cost of video" pill has something to show.
   * Demo runs are excluded from Statistics, so these never pollute real spend.
   */
  simulatedCost: { image: number; video: number };
  scenes: DemoScene[];
  /** The finished cuts shown at the end. A Drive share link works. */
  finalVideoUrl: string;
  finalVideoUrlUncaptioned: string;
}

export interface AppSettings {
  /**
   * Presentation assets used by demo campaigns. Optional: installs upgraded
   * before this existed have no `demo` settings row until setup re-runs.
   */
  demo?: DemoBank;
  /**
   * USD per generation, keyed by model id (e.g. "veo3_fast": 0.4). A model
   * with no entry costs 0. Optional: installs upgraded before this existed
   * have no `costs` settings row until setup re-runs.
   */
  costs?: Record<string, number>;
  prompts: {
    scriptWriter: string;
    customScriptSegmenter: string;
    imageStyle: string;
    imageProductClause: string;
    imageRoles: string;
    imageSubjectAnchor: string;
    imageNegative: string;
    videoDirection: string;
    videoConstraints: string;
    subtitleFix: string;
  };
  models: {
    imageModel: string;
    imageResolution: string;
    imageOutputFormat: string;
    videoModel: string;
    videoResolution: string;
    videoClipSeconds: number;
    geminiScriptModel: string;
    /**
     * Fallback chain for splitting a pasted script: attempt 1 uses the first
     * model, and each failed attempt (bad output OR the model not existing)
     * moves to the next.
     */
    geminiSegmenterModels: string[];
    /** Legacy single-model setting; used only if the chain above is empty. */
    geminiSegmenterModel?: string;
    geminiSubtitleModel: string;
    whisperModel: string;
  };
  pipeline: {
    pollIntervalSeconds: number;
    maxPollMinutes: number;
    imageRetryLimit: number;
    videoRetryLimit: number;
    geminiRetryLimit: number;
    reviewStaleAfterHours: number;
    maxWordsPerScene: number;
    acronymAllowlist: string[];
  };
  subtitles: Record<AspectRatio, SubtitleLayout>;
  storage: {
    driveFolderImages: string;
    driveFolderVideos: string;
    driveFolderFinal: string;
    /**
     * Google Sheet that gets one appended row per published video
     * (Creator / Brand / Product / AI Character / Format / Date / Video link).
     * Empty = no sheet logging. The sheet must be shared (editor) with the
     * service account email. Optional: pre-existing installs have no key
     * until an admin adds it to the storage settings JSON.
     */
    logSpreadsheetId?: string;
    tempRetentionHours: number;
    logRetentionDays: number;
  };
  rateLimits: {
    /** Requests per minute per provider, shared across all concurrent campaigns. */
    kie: number;
    gemini: number;
    groq: number;
  };
  notifications: {
    notifyOnFailure: boolean;
    notifyEmail: string;
    webhookUrl: string;
  };
}

export const SECRET_KEYS = [
  'KIE_API_KEY',
  'GEMINI_API_KEY',
  'GROQ_API_KEY',
  'OPENROUTER_API_KEY',
] as const;

export type SecretKey = (typeof SECRET_KEYS)[number];

export interface SecretMeta {
  key: string;
  hint: string;
  updated: string;
  updated_by: string;
}
